import React from 'react';
import { ExternalLink, Minus, Plus } from 'lucide-react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { openPdfDocument } from '../utils/loadPdfjs';
import { fetchPdfBytes } from '../utils/pdfDocumentBytes';
import {
  PDF_ZOOM_MAX,
  PDF_ZOOM_MIN,
  PDF_ZOOM_STEP,
  clampPdfZoom,
  nudgePdfZoom,
  pinchDistance,
} from '../utils/pdfDocumentZoom';
import { InlineLoadingState } from './LoadingSpinner';

type PdfPageCanvasProps = {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  eager: boolean;
};

function PdfPageCanvas({ pdf, pageNumber, width, eager }: PdfPageCanvasProps) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = React.useState(eager);
  const [aspect, setAspect] = React.useState(1.414);

  React.useEffect(() => {
    if (eager) {
      setVisible(true);
      return;
    }
    const el = hostRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
      },
      { rootMargin: '240px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [eager]);

  React.useEffect(() => {
    if (!visible || width < 8) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    let renderTask: RenderTask | null = null;

    void (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      setAspect(base.height / base.width);
      const cssWidth = Math.max(1, Math.floor(width));
      const cssScale = cssWidth / base.width;
      const cssHeight = Math.max(1, Math.floor(base.height * cssScale));
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      const outputScale = Math.min(3, Math.max(dpr, cssWidth < 640 ? 2.75 : 2));
      const viewport = page.getViewport({ scale: cssScale });
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) return;
      canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
      canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      renderTask = page.render({
        canvas,
        canvasContext: context,
        viewport,
        transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
      });
      try {
        await renderTask.promise;
      } catch {
        if (!cancelled) {
          /* cancelled render throws; ignore */
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [visible, pdf, pageNumber, width]);

  return (
    <div
      ref={hostRef}
      className="overflow-hidden bg-white shadow-sm ring-1 ring-black/5"
      style={visible ? undefined : { minHeight: Math.max(80, Math.round(width * aspect)) }}
    >
      {visible ? <canvas ref={canvasRef} className="block max-w-full" /> : null}
    </div>
  );
}

type Props = {
  url: string;
  title?: string;
  className?: string;
  openUrl?: string;
  onError?: (message: string) => void;
  /** Statt „Wird geladen…“, z. B. der Seiten-Platzhalter der Vorschau, damit beim Wechsel nichts aufblitzt. */
  loadingFallback?: React.ReactNode;
};

export function PdfDocumentViewer({ url, title, className, openUrl, onError, loadingFallback }: Props) {
  const [status, setStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [pdf, setPdf] = React.useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = React.useState(0);
  const [fitWidth, setFitWidth] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const onErrorRef = React.useRef(onError);
  const zoomRef = React.useRef(1);
  const pinchRef = React.useRef<{ distance: number; zoom: number } | null>(null);
  onErrorRef.current = onError;
  zoomRef.current = zoom;
  const fallbackHref = openUrl || url;
  const pageWidth = Math.max(8, Math.floor(fitWidth * zoom));

  React.useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setStatus('loading');
    setPdf(null);
    setPageCount(0);
    setZoom(1);

    void (async () => {
      try {
        const bytes = await fetchPdfBytes(url);
        const doc = await openPdfDocument(bytes);
        if (cancelled) {
          void doc.destroy();
          return;
        }
        loaded = doc;
        setPdf(doc);
        setPageCount(doc.numPages);
        setStatus('ready');
      } catch (err) {
        console.error('[PdfDocumentViewer]', err);
        if (!cancelled) {
          setStatus('error');
          onErrorRef.current?.('PDF konnte nicht geladen werden.');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (loaded) void loaded.destroy();
    };
  }, [url]);

  React.useEffect(() => {
    if (status !== 'ready') return;
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const pad = Number.parseFloat(getComputedStyle(el).paddingLeft) + Number.parseFloat(getComputedStyle(el).paddingRight);
      const next = Math.floor(el.clientWidth - (Number.isFinite(pad) ? pad : 24));
      setFitWidth(next > 8 ? next : Math.floor(el.clientWidth));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  React.useEffect(() => {
    if (status !== 'ready') return;
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      setZoom((value) => nudgePdfZoom(value, event.deltaY > 0 ? -PDF_ZOOM_STEP : PDF_ZOOM_STEP));
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) {
        pinchRef.current = null;
        return;
      }
      pinchRef.current = {
        distance: pinchDistance(event.touches[0], event.touches[1]),
        zoom: zoomRef.current,
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      const pinch = pinchRef.current;
      if (!pinch || event.touches.length !== 2) return;
      event.preventDefault();
      const next = pinch.distance > 0
        ? pinch.zoom * (pinchDistance(event.touches[0], event.touches[1]) / pinch.distance)
        : pinch.zoom;
      setZoom(clampPdfZoom(next));
    };

    const onTouchEnd = () => {
      pinchRef.current = null;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [status]);

  return (
    <div className={`flex min-h-0 min-w-0 flex-1 flex-col bg-app-surface-muted ${className ?? ''}`.trim()}>
      {status === 'loading' ? (
        loadingFallback ?? (
          <div className="grid flex-1 place-items-center p-6">
            <InlineLoadingState label="Wird geladen…" />
          </div>
        )
      ) : null}
      {status === 'error' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-sm text-app-text-muted">
            PDF konnte nicht in der Vorschau angezeigt werden.
          </p>
          <a
            href={fallbackHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-9 items-center gap-2 rounded-md bg-pipedrive-blue px-3.5 text-sm font-medium text-white hover:bg-blue-600"
          >
            <ExternalLink className="h-4 w-4" />
            In neuem Tab öffnen
          </a>
        </div>
      ) : null}
      {status === 'ready' && pdf ? (
        <div className="group/pdf relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            className="pointer-events-none absolute top-2 right-2 z-10 flex h-8 items-center overflow-hidden rounded-md border border-app-border/70 bg-app-surface/90 shadow-sm backdrop-blur-sm opacity-0 transition-opacity duration-150 group-hover/pdf:pointer-events-auto group-hover/pdf:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100 max-md:pointer-events-auto max-md:opacity-100"
            title="Strg + Mausrad oder zwei Finger zum Zoomen"
          >
            <button
              type="button"
              className="inline-flex h-full w-8 items-center justify-center text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text disabled:opacity-35"
              onClick={() => setZoom((value) => nudgePdfZoom(value, -PDF_ZOOM_STEP))}
              disabled={zoom <= PDF_ZOOM_MIN}
              aria-label="Herauszoomen"
              title="Herauszoomen"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="h-full min-w-[2.75rem] border-x border-app-border/70 px-1.5 text-[11px] font-medium tabular-nums text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted"
              onClick={() => setZoom(1)}
              title="Auf 100 % zurücksetzen"
              aria-label={`Zoom ${Math.round(zoom * 100)} Prozent, Klick setzt auf 100 Prozent`}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="inline-flex h-full w-8 items-center justify-center text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text disabled:opacity-35"
              onClick={() => setZoom((value) => nudgePdfZoom(value, PDF_ZOOM_STEP))}
              disabled={zoom >= PDF_ZOOM_MAX}
              aria-label="Hereinzoomen"
              title="Hereinzoomen"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <div
            ref={scrollRef}
            className="app-scrollbar min-h-0 min-w-0 flex-1 overflow-auto p-3"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === '+' || event.key === '=') {
                event.preventDefault();
                setZoom((value) => nudgePdfZoom(value, PDF_ZOOM_STEP));
              } else if (event.key === '-' || event.key === '_') {
                event.preventDefault();
                setZoom((value) => nudgePdfZoom(value, -PDF_ZOOM_STEP));
              } else if (event.key === '0') {
                event.preventDefault();
                setZoom(1);
              }
            }}
          >
            <div className="mx-auto flex flex-col gap-3" style={{ width: pageWidth }}>
              <span className="sr-only">
                {title ? `${title}, ` : ''}
                {pageCount} {pageCount === 1 ? 'Seite' : 'Seiten'}
              </span>
              {Array.from({ length: pageCount }, (_, index) => (
                <PdfPageCanvas
                  key={`${url}-${index + 1}`}
                  pdf={pdf}
                  pageNumber={index + 1}
                  width={pageWidth}
                  eager={index === 0}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
